import React from 'react'
import { useParams, Link, Navigate } from 'react-router-dom'
import { DynamicControlPanel } from '../components/DynamicControlPanel'
import { useExampleEditor } from '../hooks/useExampleEditor'
import { getExampleById, getCategoryBySlug } from '../gallery/registry'
import { EXAMPLE_FIELDS } from '../gallery/fieldDefinitions'
import { ExampleCodeModal } from '../components/ExampleCodeModal'

export function ExampleEditorPage() {
  const { exampleId } = useParams<{ exampleId: string }>()
  const example = exampleId ? getExampleById(exampleId) : undefined
  const fields = exampleId ? EXAMPLE_FIELDS[exampleId] ?? [] : []

  if (!example) {
    return <Navigate to="/motion-playground" replace />
  }

  return <ExampleEditorInner example={example} fields={fields} />
}

function ExampleEditorInner({
  example,
  fields,
}: {
  example: NonNullable<ReturnType<typeof getExampleById>>
  fields: typeof EXAMPLE_FIELDS[string]
}) {
  const { values, setValue, resetAll, replayKey, replay } = useExampleEditor(fields)
  const category = getCategoryBySlug(example.category)
  const Component = example.component

  return (
    <div className="h-[calc(100vh-2rem)] flex flex-col bg-gray-50">
      <header className="h-14 border-b border-gray-200 bg-white flex items-center justify-between px-4 shrink-0">
        <div className="flex items-center gap-3">
          <Link
            to="/motion-playground"
            className="flex items-center gap-1.5 text-xs text-gray-500 hover:text-gray-800 transition-colors font-medium"
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Gallery
          </Link>
          <span className="text-gray-300">|</span>
          <div className="flex items-center gap-2">
            {category && (
              <span className={`${category.color} text-white text-[9px] font-medium px-1.5 py-0.5 rounded-full`}>
                {category.label}
              </span>
            )}
            <span className="text-sm font-semibold text-gray-900">{example.title}</span>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <Link
            to="/motion-playground/sandbox"
            className="text-xs text-gray-500 hover:text-gray-800 transition-colors font-medium"
          >
            Sandbox
          </Link>
        </div>
      </header>

      <div className="flex-1 flex overflow-hidden">
        <DynamicControlPanel
          fields={fields}
          values={values}
          onChange={setValue}
          onReplay={replay}
          onReset={resetAll}
        />

        <main className="flex-1 flex flex-col relative overflow-hidden">
          <div className="flex-1 relative overflow-auto">
            {/* Dot grid background */}
            <div
              className="absolute inset-0 pointer-events-none"
              style={{
                backgroundImage: 'radial-gradient(circle, #d1d5db 1px, transparent 1px)',
                backgroundSize: '20px 20px',
              }}
            />
            <div className="relative h-full" key={replayKey}>
              <Component values={values} />
            </div>
            <ExampleCodeModal exampleId={example.id} values={values} />
          </div>
        </main>
      </div>

      <footer className="border-t border-gray-200 bg-white shrink-0">
        <div className="px-4 sm:px-6 lg:px-8 py-3">
          <p className="text-xs text-gray-500 text-center">
            Built with{' '}
            <a
              href="https://motion.dev"
              target="_blank"
              rel="noopener noreferrer"
              className="text-gray-700 hover:text-gray-900 underline"
            >
              Motion
            </a>
            {' '}by{' '}
            <a href="/" className="text-gray-700 hover:text-gray-900 underline">
              Nimble Labs
            </a>
          </p>
        </div>
      </footer>
    </div>
  )
}
